import React from 'react';
import { Link } from 'react-router-dom';
import HelpArticleLayout, { UiLabel, InfoCallout } from '../components/HelpArticleLayout';

// Help article: "How recognition badges work" — the OPTIONAL one, on the
// BRIEF-27 sections template. Eco is the growing-oak "Oak Status" progression.

const XLink = ({ children }) => (
  <Link to="/help/compliance" className="text-[#2E8FA1] hover:text-active-green font-semibold underline">
    {children}
  </Link>
);

const ICON = {
  tree:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="5.5" /><path d="M12 13.5V21" /></svg>,
  star:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15 9 22 9.3 16.5 14 18.5 21 12 17 5.5 21 7.5 14 2 9.3 9 9" /></svg>,
  refresh: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></svg>,
  sparkle: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" /></svg>,
  help:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>,
};

const sections = [
  {
    id: 'eco', title: 'The Eco badge — growing your oak', icon: ICON.tree,
    body: (
      <>
        <p className="text-[13px] text-[#7C7F6F]">Looking for your required documents instead? See <XLink>your compliance status</XLink>.</p>
        <p>The Eco badge recognises your sustainability commitment, and it grows as you go. It starts as an acorn and grows towards <strong className="text-ink">Oak Status</strong> — full recognition — as you complete the eco documents Fusion 2K asks you for.</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-fusion-mid-grey">
          <li>If only one eco document is required, completing it takes you straight to <strong className="text-ink">Oak Status</strong>.</li>
          <li>If more are required, your oak grows a stage with each one you complete, reaching <strong className="text-ink">Oak Status</strong> when they&rsquo;re all done.</li>
        </ul>
        <p>You&rsquo;ll see your progress on the Eco card — for example, &ldquo;3 of 5 eco documents approved.&rdquo; Each document has its own requirements page: click <UiLabel>View requirements</UiLabel> on the card to see what&rsquo;s needed next.</p>
      </>
    ),
  },
  {
    id: 'why', title: 'Why it matters', icon: ICON.star,
    body: (
      <ul className="list-disc pl-5 space-y-2 marker:text-fusion-mid-grey">
        <li><strong className="text-ink">Fusion sees it.</strong> When Fusion 2K compares supplier quotes side by side, your recognition shows alongside your name and price. Eco-conscious clients increasingly weigh sustainability in who they choose.</li>
        <li><strong className="text-ink">You stand out in briefs.</strong> Suppliers at <strong className="text-ink">Oak Status</strong> are prioritised when a client flags a brief as sustainability-relevant.</li>
      </ul>
    ),
  },
  {
    id: 'current', title: 'Keeping your oak current', icon: ICON.refresh,
    body: (
      <>
        <p>Your Eco badge is driven by your documents, not by a calendar — eco documents don&rsquo;t expire, so there&rsquo;s nothing to renew and your oak won&rsquo;t lapse on its own.</p>
        <p>Once you reach a stage, it stays there. The only time it changes is if Fusion 2K issues a new or refreshed eco document — you&rsquo;ll see an <UiLabel>action needed</UiLabel> prompt on your Eco card until you complete it, and your oak grows back to <strong className="text-ink">Oak Status</strong> once you have.</p>
      </>
    ),
  },
  {
    id: 'more', title: 'More recognition categories coming', icon: ICON.sparkle,
    body: (
      <p>Eco is the first recognition badge. Fusion 2K is planning more. New categories appear in the <UiLabel>Grow your recognition</UiLabel> section as they launch. (All recognition — none affect your compliance.)</p>
    ),
  },
  {
    id: 'optional', title: 'Are recognition badges optional?', icon: ICON.help,
    body: (
      <>
        <p>Completely. None of Fusion 2K&rsquo;s compliance requirements depend on badges. They&rsquo;re a way to be recognised for the extra effort you put in — never a hidden requirement. Your eligibility for work to be awarded comes from compliance, not badges.</p>
        <InfoCallout tone="positive">Recognition never replaces compliance, and compliance never depends on badges. They&rsquo;re two separate things — see <XLink>your compliance status</XLink> for the required side.</InfoCallout>
      </>
    ),
  },
];

export default function HelpRecognition() {
  return (
    <HelpArticleLayout
      breadcrumb="Recognition"
      title="How recognition badges work"
      intro="Recognition badges are how Fusion 2K recognises suppliers who go beyond the basics. They&rsquo;re optional, visible to Fusion 2K and to clients on every brief, and they sit on top of your compliance — they never replace it."
      readTime="3 min read"
      sections={sections}
      footerText="Got a question about a specific badge or requirement? Let us know."
    />
  );
}
